import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { MeetingNote } from 'src/meeting-note/types';

type SetMeetingNoteReviewedInput = {
  clientId: string;
  meetingNoteId: string;
  isReviewed: boolean;
};

export const useSetMeetingNoteReviewed = (eventId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['calendar-events', eventId, 'meeting-notes'];

  const { mutateAsync, isPending: isLoading } = useMutation<
    unknown,
    Error,
    SetMeetingNoteReviewedInput,
    { previous: MeetingNote | undefined }
  >({
    mutationFn: ({ clientId, meetingNoteId, isReviewed }) =>
      api.patch(`clients/${clientId}/meeting-notes/${meetingNoteId}/review`, { isReviewed }),
    onMutate: async ({ isReviewed }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MeetingNote>(queryKey);
      if (previous) {
        queryClient.setQueryData<MeetingNote>(queryKey, { ...previous, isReviewed });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData<MeetingNote>(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const setMeetingNoteReviewed = async (input: SetMeetingNoteReviewedInput): Promise<void> => {
    await mutateAsync(input);
  };

  return { setMeetingNoteReviewed, isLoading };
};
